// a list of api endpoints for the whole application in sync with the backend

export const apiEndpoints = {
  auth: {
    login: "/auth/login",
    register: "/auth/register",
  },
  users: {
    getAll: "/users",
    getOneById: (id: string) => `/users/${id}`,
    create: "/users",
    createDefault: "/users/default",
    update: (id: string) => `/users/${id}`,
    delete: (id: string) => `/users/${id}`,
  },
  clients: {
    getAll: "/clients",
    getOneById: (id: string) => `/clients/${id}`,
    create: "/clients",
    update: (id: string) => `/clients/${id}`,
    delete: (id: string) => `/clients/${id}`,
    getAllUsers: "/clients/users",
    getUsers: (clientId: string) => `/clients/${clientId}/users`,
    assignUser: (clientId: string) => `/clients/${clientId}/users`,
    removeUser: (clientId: string, userId: string) =>
      `/clients/${clientId}/users/${userId}`,
    createUser: (clientId: string) => `/clients/${clientId}/users/create`,
  },
  contractors: {
    getAll: "/contractors",
    getOneById: (id: string) => `/contractors/${id}`,
    create: "/contractors",
    batchCreate: "/contractors/batch",
    update: (id: string) => `/contractors/${id}`,
    delete: (id: string) => `/contractors/${id}`,
  },
  suppliers: {
    getAll: "/suppliers",
    getOneById: (id: string) => `/suppliers/${id}`,
    create: "/suppliers",
    update: (id: string) => `/suppliers/${id}`,
    delete: (id: string) => `/suppliers/${id}`,
  },
  auditLogs: {
    getAll: "/audit-logs",
    getOneById: (id: string) => `/audit-logs/${id}`,
  },
  purchaseOrders: {
    getAll: (projectId?: string) => projectId ? `/projects/${projectId}/purchase-orders` : "/purchase-orders",
    getOneById: (id: string, projectId?: string) => projectId ? `/projects/${projectId}/purchase-orders/${id}` : `/purchase-orders/${id}`,
    create: (projectId?: string) => projectId ? `/projects/${projectId}/purchase-orders` : "/purchase-orders",
    update: (id: string, projectId?: string) => projectId ? `/projects/${projectId}/purchase-orders/${id}` : `/purchase-orders/${id}`,
    delete: (id: string, projectId?: string) => projectId ? `/projects/${projectId}/purchase-orders/${id}` : `/purchase-orders/${id}`,
    markDelivered: (id: string, projectId?: string) => projectId ? `/projects/${projectId}/purchase-orders/${id}/mark-delivered` : `/purchase-orders/${id}/mark-delivered`,
    cancel: (id: string, projectId?: string) => projectId ? `/projects/${projectId}/purchase-orders/${id}/cancel` : `/purchase-orders/${id}/cancel`,
  },
  punchlists: {
    getAll: (projectId?: string) => projectId ? `/punchlists?projectId=${projectId}` : "/punchlists",
    getOneById: (id: string, projectId?: string) => projectId ? `/punchlists/${id}?projectId=${projectId}` : `/punchlists/${id}`,
    create: (projectId?: string) => projectId ? `/projects/${projectId}/punchlists/items` : "/punchlists/items",
    update: (id: string, projectId?: string) => projectId ? `/punchlists/${id}?projectId=${projectId}` : `/punchlists/${id}`,
    delete: (id: string, projectId?: string) => projectId ? `/punchlists/${id}?projectId=${projectId}` : `/punchlists/${id}`,
    assign: (id: string) => `/punchlists/${id}/assign`,
    markFixed: (id: string, projectId?: string) => projectId ? `/projects/${projectId}/punchlists/${id}/mark-fixed` : `/punchlists/${id}/mark-fixed`,
    verify: (id: string, projectId?: string) => projectId ? `/projects/${projectId}/punchlists/${id}/verify` : `/punchlists/${id}/verify`,
    rejectFix: (id: string, projectId?: string) => projectId ? `/projects/${projectId}/punchlists/${id}/reject-fix` : `/punchlists/${id}/reject-fix`,
    getPhotos: (id: string, projectId?: string) => projectId ? `/punchlists/${id}/photos?projectId=${projectId}` : `/punchlists/${id}/photos`,
    portalCreate: (projectId: string) => `/portal/projects/${projectId}/punchlists/items`,
    portalGetAll: (projectId: string) => `/portal/projects/${projectId}/punchlists`,
    portalGetOneById: (projectId: string, itemId: string) => `/portal/projects/${projectId}/punchlists/${itemId}`,
  },
  changeOrders: {
    getAll: (projectId?: string) => projectId ? `/change-orders?projectId=${projectId}` : "/change-orders",
    getOneById: (id: string, projectId?: string) => projectId ? `/change-orders/${id}?projectId=${projectId}` : `/change-orders/${id}`,
    create: (projectId?: string) => projectId ? `/change-orders?projectId=${projectId}` : "/change-orders",
    update: (id: string, projectId?: string) => projectId ? `/change-orders/${id}?projectId=${projectId}` : `/change-orders/${id}`,
    delete: (id: string, projectId?: string) => projectId ? `/change-orders/${id}?projectId=${projectId}` : `/change-orders/${id}`,
    unbilledCount: (projectId?: string) => projectId ? `/change-orders/unbilled-count?projectId=${projectId}` : "/change-orders/unbilled-count",
    getPdfUrl: (id: string) => `/change-orders/${id}/pdf`,
  },
  notifications: {
    getAll: "/notifications",
    unreadCount: "/notifications/unread-count",
    markRead: (id: string) => `/notifications/${id}/read`,
    markAllRead: "/notifications/mark-all-read",
  },
  portal: {
    projects: {
      getAll: "/portal/projects",
      getOneById: (id: string) => `/portal/projects/${id}`,
      getOverview: (id: string) => `/portal/projects/${id}/overview`,
    },
    notifications: {
      getAll: "/portal/notifications",
      unreadCount: "/portal/notifications/unread-count",
      markRead: (id: string) => `/portal/notifications/${id}/read`,
      markAllRead: "/portal/notifications/mark-all-read",
    },
    milestones: {
      getAll: (projectId: string) =>
        `/portal/projects/${projectId}/milestones`,
      getOneById: (projectId: string, milestoneId: string) =>
        `/portal/projects/${projectId}/milestones/${milestoneId}`,
    },
    dailyLogs: {
      getAll: (projectId: string) =>
        `/portal/projects/${projectId}/daily-logs`,
      getOneById: (projectId: string, logId: string) =>
        `/portal/projects/${projectId}/daily-logs/${logId}`,
    },
    materialDecisions: {
      getAll: (projectId: string) =>
        `/portal/projects/${projectId}/material-decisions`,
      getOneById: (projectId: string, decisionId: string) =>
        `/portal/projects/${projectId}/material-decisions/${decisionId}`,
      setSourcingPreference: (projectId: string, decisionId: string) =>
        `/portal/projects/${projectId}/material-decisions/${decisionId}/sourcing-preference`,
      selectOption: (projectId: string, decisionId: string) =>
        `/portal/projects/${projectId}/material-decisions/${decisionId}/select`,
      pendingCount: (projectId: string) =>
        `/portal/projects/${projectId}/material-decisions/pending-count`,
    },
    changeOrders: {
      getAll: (projectId: string) =>
        `/portal/projects/${projectId}/change-orders`,
      getOneById: (projectId: string, changeOrderId: string) =>
        `/portal/projects/${projectId}/change-orders/${changeOrderId}`,
      approve: (projectId: string, changeOrderId: string) =>
        `/portal/projects/${projectId}/change-orders/${changeOrderId}/approve`,
      reject: (projectId: string, changeOrderId: string) =>
        `/portal/projects/${projectId}/change-orders/${changeOrderId}/reject`,
      getPdfUrl: (projectId: string, changeOrderId: string) =>
        `/portal/projects/${projectId}/change-orders/${changeOrderId}/pdf`,
    },
    clientInvoices: {
      getAll: (projectId: string) =>
        `/portal/projects/${projectId}/invoices`,
      getOneById: (projectId: string, invoiceId: string) =>
        `/portal/projects/${projectId}/invoices/${invoiceId}`,
      reportPayment: (projectId: string, invoiceId: string) =>
        `/portal/projects/${projectId}/invoices/${invoiceId}/report-payment`,
      getAllForClient: "/portal/invoices",
      getDetail: (invoiceId: string) => `/portal/invoices/${invoiceId}`,
    },
  },
};
